/**
 * Native sign-in attempt registry · TKT #274563 (direct-native Google).
 *
 * Direct-native Google no longer uses the Emergent broker or a deep-link
 * callback: the Google Sign-In SDK returns an id_token in-process whose signed
 * `nonce` claim == the raw server nonce this app requested, so the result IS
 * cryptographically bound to the launching attempt. We still (a) bind to the
 * app launch, (b) allow only ONE flow at a time, and (c) FAIL CLOSED on any
 * ambiguity, and — critically — (d) bind nonce attachment, cancellation and
 * consumption to the EXACT attempt id captured at launch, so a slow/stale
 * /start response, result or error from an older attempt can never modify or
 * cancel a newer one.
 *
 *   beginSignInAttempt(provider)       -> id on a clean start; null if a flow is
 *                                         already in progress (poisons it).
 *   setAttemptServerNonce(id, nonce)   -> attach nonce ONLY if id is still current.
 *   consumeSignInAttempt(provider,{id})-> the attempt, else null. Single-use.
 *   cancelSignInAttempt(id?)           -> clear the pending attempt; when id is
 *                                         given, only if it is still the current one.
 */
const K_ATTEMPT = "cc.auth.signin.attempt";
const DEFAULT_MAX_AGE_MS = 5 * 60 * 1000;     // result must arrive within 5 min
const IN_PROGRESS_TTL_MS = 2 * 60 * 1000;     // a flow is "in progress" for 2 min

function _rand() {
  try {
    const a = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return String(Date.now()) + Math.random().toString(16).slice(2);
  }
}

// Regenerated on every module evaluation => unique per JS context / app launch.
let _appSessionId = _rand();

function _read() {
  try { const r = window.sessionStorage.getItem(K_ATTEMPT); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
function _write(a) { try { window.sessionStorage.setItem(K_ATTEMPT, JSON.stringify(a)); } catch { /* */ } }

export function peekSignInAttempt() { return _read(); }

/**
 * Start a flow. Returns an id on a clean start. If a flow for this app launch
 * is still in progress, returns null AND marks the flow ambiguous so NO
 * callback (old or new) can complete — the caller must not open a browser.
 */
export function beginSignInAttempt(provider, { serialize = false } = {}) {
  const cur = _read();
  const inProgress = cur && cur.app_session === _appSessionId &&
    cur.created_at && (Date.now() - cur.created_at) <= IN_PROGRESS_TTL_MS;
  if (serialize && inProgress) {
    _write({ ...cur, ambiguous: true });   // overlap => poison; both callbacks fail closed
    return null;
  }
  const attempt = { id: _rand(), provider, created_at: Date.now(), app_session: _appSessionId, ambiguous: false };
  _write(attempt);
  return attempt.id;
}

/** Validate + consume (single-use). Fails closed on any ambiguity. */
export function consumeSignInAttempt(provider, { maxAgeMs = DEFAULT_MAX_AGE_MS, id = null } = {}) {
  const attempt = _read();
  // Bind-BEFORE-clear: a stale result whose attempt id does NOT match the
  // current registry entry must leave that (newer) attempt completely untouched
  // so it can still complete. Only a matching id (or an id-less legacy caller)
  // consumes/poisons the entry.
  if (id && (!attempt || typeof attempt !== "object" || attempt.id !== id)) return null;
  cancelSignInAttempt(); // single-use: consume (or poison) only the matching attempt
  if (!attempt || typeof attempt !== "object") return null;
  if (attempt.ambiguous) return null;                       // overlapping flows => refuse
  if (attempt.app_session !== _appSessionId) return null;   // different app launch / cold start
  if (provider && attempt.provider !== provider) return null;
  if (!attempt.created_at || (Date.now() - attempt.created_at) > maxAgeMs) return null;
  return attempt;
}

/**
 * Attach the server-issued nonce to the pending attempt — but ONLY when `id`
 * is still the current attempt. A slow /google/start response from a previous
 * (now superseded) attempt must never overwrite a newer attempt's nonce.
 * Returns true iff the nonce was attached to the still-current attempt; false
 * means the attempt was cancelled or superseded while /start was pending, and
 * the caller MUST stop before invoking the native SDK.
 */
export function setAttemptServerNonce(id, serverNonce) {
  const cur = _read();
  if (!cur || cur.id !== id) return false;
  _write({ ...cur, server_nonce: serverNonce });
  return true;
}

/**
 * Clear the pending attempt. When `id` is given (an attempt's own error/back
 * path) only clear if it is still the current attempt, so a stale error cannot
 * cancel a newer flow. With no id (logout) clear unconditionally.
 */
export function cancelSignInAttempt(id = null) {
  if (id) {
    const cur = _read();
    if (!cur || cur.id !== id) return;
  }
  try { window.sessionStorage.removeItem(K_ATTEMPT); } catch { /* */ }
}

// Back-compat name used on logout — unconditional clear.
export const invalidateSignInAttempt = () => cancelSignInAttempt();

export const __test__ = {
  K_ATTEMPT,
  IN_PROGRESS_TTL_MS,
  simulateColdStart: () => { _appSessionId = _rand(); },
};
